'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedSize from 'I18n/components/FormattedSize';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import H1 from 'UIComponents/elements/headings/H1';
import UIFlex from 'UIComponents/layout/UIFlex';
import PracticalExerciseCondensedWrapper from './PracticalExerciseCondensedWrapper';
import FileUploadComponent from 'FileManagerLib/components/FileUploadComponent';
import { DropZoneNoBrowseFileAccess } from 'FileManagerLib/enums/FileAccess';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIFileButton from 'UIComponents/button/UIFileButton';
import UILink from 'UIComponents/link/UILink';
import styled from 'styled-components';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIFileAttachment from 'UIComponents/attachment/UIFileAttachment';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import TextHR from '../../../components/TextHR';
import UITextInput from 'UIComponents/input/UITextInput';
import UIValidatedUrlFormControl from 'ui-addon-form-validation/Url/UIValidatedUrlFormControl';
import FileExtensionFilters from 'FileManagerLib/enums/FileExtensionFilters';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { SubmissionAssetTypes } from '../';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INITIAL_FILE_STATE = {
  fileUrl: null,
  importedFile: undefined
};
const ACCEPTED_TYPES = ['mp4', 'html', 'css', 'pdf', 'jpg', 'jpeg', 'png', 'mpg', 'mpeg', 'xls', 'xlsx', 'docx', 'doc'];
const ACCEPTED_TYPES_HUMAN = 'MP4, HTML, CSS, PDF, Word, Excel, JPEG, PNG';
const URL_VALIDATION_MESSAGES = {
  empty: '',
  validating: '',
  success: '',
  error: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.practicalExercise.invalidUrl"
  })
};
const FileUploadStyleWrapper = styled.div.withConfig({
  displayName: "PracticalExerciseWithSubmission__FileUploadStyleWrapper",
  componentId: "sc-1t8xpzv-0"
})(["[data-icon-name='file']{margin-top:16px;}position:relative;opacity:", ";.file-upload-blocking-overlay{display:", ";position:absolute;top:0;left:0;right:0;bottom:0;}.file-attachment-component{position:absolute;top:0;left:0;right:0;bottom:0;}.drop-zone-target{opacity:", ";}"], ({
  disabled
}) => disabled ? 0.3 : 1, ({
  disabled
}) => disabled ? 'block' : 'none', ({
  hasFile
}) => hasFile ? 0 : 1);
const SubmissionLinkStyleWrapper = styled.div.withConfig({
  displayName: "PracticalExerciseWithSubmission__SubmissionLinkStyleWrapper",
  componentId: "sc-1t8xpzv-1"
})(["position:relative;opacity:", ";.submission-link-blocking-overlay{display:", ";position:absolute;top:0;left:0;right:0;bottom:0;}"], ({
  disabled
}) => disabled ? 0.3 : 1, ({
  disabled
}) => disabled ? 'block' : 'none');
const PracticalExerciseWithSubmission = ({
  advanceToNextContent,
  isPassRequiredForCert,
  title,
  onSubmit,
  isSubmitting
}) => {
  const [fileState, setFileState] = useState(INITIAL_FILE_STATE);
  const [importedFileObject, setImportedFileObject] = useState();
  const [fileTooltipTarget, setFileTooltipTarget] = useState();
  const [submissionLink, setSubmissionLink] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const handleOnFileChange = useCallback((fileUrl, importedFile) => {
    setFileState({
      fileUrl,
      importedFile
    });
  }, []);
  const handleOnFileAdd = useCallback(file => {
    setImportedFileObject(file);
  }, []);
  const handleCloseClick = useCallback(() => {
    setFileState(INITIAL_FILE_STATE);
    setImportedFileObject();
  }, []);
  const handleOnLinkInputChange = useCallback(e => {
    setSubmissionLink(e.target.value);
  }, []);
  const {
    importedFile
  } = fileState;
  const handleOnSubmitClick = useCallback(() => {
    const assetType = importedFile ? SubmissionAssetTypes.FILE_ID : SubmissionAssetTypes.SUBMISSION_URL;
    const reference = importedFile ? importedFile.get('id') : submissionLink;
    onSubmit(assetType, reference);
  }, [importedFile, onSubmit, submissionLink]);
  return /*#__PURE__*/_jsx(PracticalExerciseCondensedWrapper, {
    buttons: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        className: "m-right-3",
        "data-test-id": "skip-nonstarted-practical-exercise",
        onClick: advanceToNextContent,
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.skip"
        })
      }), /*#__PURE__*/_jsx(UILoadingButton, {
        "data-test-id": "practical-exercise-review-button",
        disabled: submissionLink && !isValidUrl || !submissionLink && !importedFile,
        loading: isSubmitting,
        onClick: handleOnSubmitClick,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.submit"
        })
      })]
    }),
    children: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      "data-test-id": "not-started-practical-exercise-manual-submission",
      direction: "column",
      children: [/*#__PURE__*/_jsx(H1, {
        style: {
          textAlign: 'center'
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.manualPETitle",
          options: {
            title
          }
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practicalExercise.uploadOrLink"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practicalExercise.submittedGradingTime"
      }), !!isPassRequiredForCert || /*#__PURE__*/_jsx(FormattedMessage, {
        className: "m-bottom-5",
        message: "learningCenter.practicalExercise.reminder.required"
      }), /*#__PURE__*/_jsxs(UIFlex, {
        align: "stretch",
        direction: "column",
        children: [/*#__PURE__*/_jsxs(FileUploadStyleWrapper, {
          disabled: submissionLink ? 1 : 0,
          hasFile: importedFile ? 1 : 0,
          children: [/*#__PURE__*/_jsx(FileUploadComponent, {
            closeable: true,
            fileData: importedFile && importedFile.get('id') ? importedFile : undefined,
            fileUploadOptions: {
              overwrite: false,
              folderPath: 'academy-practical-exercise-file-submissions'
            },
            filterType: FileExtensionFilters.SUPPORTED,
            filteredExtensions: ACCEPTED_TYPES,
            iconName: importedFile ? 'file' : 'upload',
            onChange: handleOnFileChange,
            onClose: handleCloseClick,
            onFileAdd: handleOnFileAdd,
            showFileData: false,
            showFileName: false,
            uploadButtonRenderer: handleUploadClick => /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              direction: "column",
              children: [/*#__PURE__*/_jsxs("span", {
                children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "learningCenter.practicalExercise.dragAndDrop",
                  options: {
                    browse: /*#__PURE__*/_jsx(UIFileButton, {
                      Button: UILink,
                      onChange: e => {
                        handleUploadClick(e);
                        e.target.value = null;
                      },
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.practicalExercise.dragAndDropBrowse"
                      })
                    })
                  }
                }), /*#__PURE__*/_jsx(UIHelpIcon, {
                  className: "m-left-1",
                  title: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.practicalExercise.acceptedTypes",
                    options: {
                      fileTypes: ACCEPTED_TYPES_HUMAN
                    }
                  }),
                  tooltipPlacement: "right"
                })]
              }), /*#__PURE__*/_jsx("div", {
                ref: ref => setFileTooltipTarget(ref),
                children: /*#__PURE__*/_jsx(UIFileButton, {
                  className: "m-top-3",
                  "data-test-id": "practical-exercise-upload-button",
                  onChange: e => {
                    handleUploadClick(e);
                    e.target.value = null;
                  },
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.practicalExercise.upload"
                  })
                })
              })]
            }),
            uploadedFileAccess: DropZoneNoBrowseFileAccess.HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE,
            children: importedFileObject && /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              direction: "column",
              children: [/*#__PURE__*/_jsxs("span", {
                children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "learningCenter.practicalExercise.dragAndDrop",
                  options: {
                    browse: /*#__PURE__*/_jsx(UIFileButton, {
                      Button: UILink,
                      disabled: true,
                      onChange: () => {},
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.practicalExercise.dragAndDropBrowse"
                      })
                    })
                  }
                }), /*#__PURE__*/_jsx(UIHelpIcon, {
                  className: "m-left-1",
                  title: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.practicalExercise.acceptedTypes",
                    options: {
                      fileTypes: ACCEPTED_TYPES_HUMAN
                    }
                  }),
                  tooltipPlacement: "right"
                })]
              }), /*#__PURE__*/_jsx("div", {
                ref: ref => setFileTooltipTarget(ref),
                children: /*#__PURE__*/_jsx(UIFileButton, {
                  className: "m-top-3",
                  disabled: true,
                  onChange: () => {},
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.practicalExercise.upload"
                  })
                })
              })]
            })
          }), importedFile && importedFileObject && /*#__PURE__*/_jsx(UIFlex, {
            align: "center",
            className: "file-attachment-component",
            justify: "center",
            children: /*#__PURE__*/_jsx(UIFileAttachment, {
              closeable: true,
              fileInfo: /*#__PURE__*/_jsx(FormattedSize, {
                value: importedFileObject.size
              }),
              fileName: importedFileObject.name,
              fileType: importedFileObject.type,
              onCloseClick: handleCloseClick,
              processing: importedFileObject && !importedFile,
              children: /*#__PURE__*/_jsx(UINanoProgress, {
                boxShadow: false
              })
            })
          }), /*#__PURE__*/_jsx(UITooltip, {
            target: fileTooltipTarget,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.practicalExercise.removeLink"
            }),
            children: /*#__PURE__*/_jsx("div", {
              className: "file-upload-blocking-overlay"
            })
          })]
        }), /*#__PURE__*/_jsx(TextHR, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.or",
            style: {
              opacity: importedFileObject || submissionLink ? 0.5 : 1
            }
          })
        }), /*#__PURE__*/_jsxs(SubmissionLinkStyleWrapper, {
          disabled: importedFileObject ? 1 : 0,
          children: [/*#__PURE__*/_jsx(UIValidatedUrlFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.practicalExercise.addSubmissionLink"
            }),
            onError: () => setIsValidUrl(false),
            onSuccess: () => setIsValidUrl(true),
            validationMessage: URL_VALIDATION_MESSAGES,
            children: /*#__PURE__*/_jsx(UITextInput, {
              "data-test-id": "practical-exercise-url-field",
              name: "submission-link",
              onChange: handleOnLinkInputChange,
              value: submissionLink
            })
          }), /*#__PURE__*/_jsx(UITooltip, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.practicalExercise.removeFile"
            }),
            children: /*#__PURE__*/_jsx("div", {
              className: "submission-link-blocking-overlay"
            })
          })]
        })]
      })]
    })
  });
};
PracticalExerciseWithSubmission.propTypes = {
  advanceToNextContent: PropTypes.func.isRequired,
  isPassRequiredForCert: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
};
export default PracticalExerciseWithSubmission;